import type React from "react"
import { useEffect, useState, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import { Separator } from "../../components/ui/separator"
import FirmInformationCard from "../../components/forms/FirmInformationCard"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { DeleteModal } from "../../components/modals/DeleteModal"
import { firmsApi } from "../../services/firmsApi"

interface FirmDetails {
  id: string
  first_name: string
  last_name: string
  name: string
  email: string
  law_firm: string
  state: string
  created_at?: string
  updated_at?: string
}

export default function FirmDetail() {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [firmDetails, setFirmDetails] = useState<FirmDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editingData, setEditingData] = useState<FirmDetails | null>(null)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [deleteFirmModal, setDeleteFirmModal] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [editErrors, setEditErrors] = useState<{ [key: string]: string[] }>({})

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = useState("")

  const fetchFirmDetails = useCallback(async (firmId: string) => {
    try {
      setLoading(true)
      const response = await firmsApi.getFirm(firmId)
      
      // Check if response has the expected structure
      if (response && response.firm) {
        setFirmDetails(response.firm)
      } else if (response && typeof response === 'object' && 'id' in response && 'first_name' in response) {
        // If the response is the firm object directly
        setFirmDetails(response as unknown as FirmDetails)
      } else {
        console.error('Unexpected API response structure:', response)
        setFirmDetails(null)
      }
    } catch (error) {
      console.error('Failed to fetch firm details:', error)
      setFirmDetails(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (state?.firmId) {
      fetchFirmDetails(state.firmId)
    } else {
      setFirmDetails(null)
      setLoading(false)
    }
  }, [state?.firmId, fetchFirmDetails])

  // Debounced email availability check for editing
  useEffect(() => {
    if (!isEditing || !editingData?.email || editingData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // Use a single timer for all validation (format + availability)
    const timer = setTimeout(() => {
      // First validate email format using the same regex as AddFirm.tsx
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
      
      if (!emailRegex.test(editingData.email)) {
        setEmailAvailable(false)
        setEmailMessage("Please enter a valid email address")
        return
      }

      // Additional email format checks
      const [localPart, domain] = editingData.email.split('@')
      if (localPart.length > 64) {
        setEmailAvailable(false)
        setEmailMessage("Email local part cannot exceed 64 characters")
        return
      }
      if (domain.length > 253) {
        setEmailAvailable(false)
        setEmailMessage("Email domain cannot exceed 253 characters")
        return
      }
      if (localPart.length === 0) {
        setEmailAvailable(false)
        setEmailMessage("Email local part cannot be empty")
        return
      }
      if (domain.length === 0) {
        setEmailAvailable(false)
        setEmailMessage("Email domain cannot be empty")
        return
      }

      // Only call API if email format is valid
      const checkAvailability = async () => {
        try {
          setEmailChecking(true)
          const response = await firmsApi.checkFirmEmailAvailability(editingData.email, firmDetails?.id)
          setEmailAvailable(response.available)
          setEmailMessage(response.message)
          
          // Clear email error if email becomes available
          if (response.available && editErrors.email) {
            setEditErrors(prev => ({ ...prev, email: [] }))
          }
        } catch (error) {
          console.error('Email availability check failed:', error)
          setEmailAvailable(null)
          setEmailMessage("Unable to verify email")
        } finally {
          setEmailChecking(false)
        }
      }

      checkAvailability()
    }, 500) // 500ms delay for all validation

    return () => clearTimeout(timer)
  }, [editingData?.email, isEditing, firmDetails?.id, editErrors.email])





  const handleBack = () => {
    navigate("/app/firms")
  }

  const handleEdit = () => {
    setIsEditing(true)
    setEditingData(firmDetails)
    // Reset email validation when starting to edit
    setEmailAvailable(null)
    setEmailMessage("")
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditingData(null)
    setEditErrors({})
    // Reset email validation when canceling edit
    setEmailAvailable(null)
    setEmailMessage("")
  }

  const handleSaveEdit = () => {
    if (!editingData) return
    
    // Validate required fields
    const errors: { [key: string]: string[] } = {}
    
    // First name validation
    if (!editingData.first_name?.trim()) {
      errors.first_name = ["First name is required"]
    } else if (editingData.first_name.length > 50) {
      errors.first_name = ["First name cannot exceed 50 characters"]
    }
    
    // Last name validation
    if (!editingData.last_name?.trim()) {
      errors.last_name = ["Last name is required"]
    } else if (editingData.last_name.length > 50) {
      errors.last_name = ["Last name cannot exceed 50 characters"]
    }
    
    // Email validation (required)
    if (!editingData.email?.trim()) {
      errors.email = ["Email is required"]
    } else if (editingData.email.length > 255) {
      errors.email = ["Email cannot exceed 255 characters"]
    } else {
      // Use the same email regex as AddFirm.tsx
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
      if (!emailRegex.test(editingData.email)) {
        errors.email = ["Invalid email format"]
      }
    }
    
    // Law firm validation
    if (!editingData.law_firm?.trim()) {
      errors.law_firm = ["Law firm is required"]
    } else if (editingData.law_firm.length > 255) {
      errors.law_firm = ["Law firm name cannot exceed 255 characters"]
    }
    
    // State validation
    if (!editingData.state?.trim()) {
      errors.state = ["State is required"]
    }
    
    // Check email availability before allowing save
    if (emailAvailable !== true) {
      errors.email = ["Please ensure the email is available before submitting"]
    }
    
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors)
      return
    }
    
    // Clear errors if validation passes
    setEditErrors({})
    setShowSaveDialog(true)
  }

  const confirmSave = async () => {
    if (!editingData || !firmDetails) {
      setShowSaveDialog(false)
      return
    }

    try {
      // Prepare the data for the API call (exclude id and timestamps)
      const updateData = {
        first_name: editingData.first_name,
        last_name: editingData.last_name,
        email: editingData.email,
        law_firm: editingData.law_firm,
        state: editingData.state,
      }

      // Make the API call to update the firm
      const response = await firmsApi.updateFirm(firmDetails.id, updateData)
            
      // Update the local state with the response data
      if (response.firm) {
        setFirmDetails(response.firm)
      } else {
        // Fallback: update with editing data
        setFirmDetails(editingData)
      }
      
      // Reset editing state
      setIsEditing(false)
      setEditingData(null)
      setShowSaveDialog(false)
      setEditErrors({})
      // Reset email validation
      setEmailAvailable(null)
      setEmailMessage("")
      
    } catch (error) {
      console.error('Failed to update firm:', error)
      // Handle API errors - you might want to show a toast notification here
      // For now, we'll keep the dialog open and let the user try again
    }
  }

  const cancelSave = () => {
    setShowSaveDialog(false)
  }



  const deleteFirmModalClose = () => {
    setDeleteFirmModal(false)
    setSelectedId("")
  }

  const deleteItem = async () => {
    try {
      await firmsApi.deleteFirm(selectedId)
      deleteFirmModalClose()
      navigate("/app/firms")
    } catch (error) {
      console.error('Failed to delete firm:', error)
    }
  }

  const handleDataChange = (field: string, value: any) => {
    if (editingData) {
      const processedValue = field === 'email' ? value.replace(/\s+$/, '') : value
      setEditingData({ ...editingData, [field]: processedValue })
      
      // Clear error for this field when user starts typing
      if (editErrors[field]) {
        setEditErrors(prev => ({ ...prev, [field]: [] }))
      }
      
      // Reset validation states when email changes
      if (field === 'email') {
        setEmailAvailable(null)
        setEmailMessage("")
        // Clear any existing email errors when email field changes
        if (editErrors.email) {
          setEditErrors(prev => ({ ...prev, email: [] }))
        }
      }

      // Real-time validation
      const newErrors = { ...editErrors }
      
      // First name validation
      if (field === 'first_name') {
        if (processedValue.length > 50) {
          newErrors.first_name = ["First name cannot exceed 50 characters"]
        } else if (newErrors.first_name) {
          delete newErrors.first_name
        }
      }
      
      // Last name validation
      if (field === 'last_name') {
        if (processedValue.length > 50) {
          newErrors.last_name = ["Last name cannot exceed 50 characters"]
        } else if (newErrors.last_name) {
          delete newErrors.last_name
        }
      }
      
      // Email validation - only check length, format validation is handled by useEffect
      if (field === 'email') {
        if (processedValue.length > 254) {
          newErrors.email = ["Email cannot exceed 254 characters"]
        } else if (newErrors.email) {
          delete newErrors.email
        }
      }
      
      // Law firm validation
      if (field === 'law_firm') {
        if (processedValue.length > 255) {
          newErrors.law_firm = ["Law firm name cannot exceed 255 characters"]
        } else if (newErrors.law_firm) {
          delete newErrors.law_firm
        }
      }
      
      setEditErrors(newErrors)
    }
  }


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading firm details...</p>
        </div>
      </div>
    )
  }

  if (!firmDetails) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Firm not found</p>
          <Button onClick={handleBack} className="mt-4">
            Back to Firms
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={handleBack} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Back to Firms</span>
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-2xl font-bold text-slate-900">Firm Details</h1>
            </div>

          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Firm Information */}
          <FirmInformationCard
            firmData={{
              first_name: editingData ? editingData.first_name : firmDetails.first_name,
              last_name: editingData ? editingData.last_name : firmDetails.last_name,
              email: editingData ? editingData.email : firmDetails.email,
              law_firm: editingData ? editingData.law_firm : firmDetails.law_firm,
              state: editingData ? editingData.state : firmDetails.state,
            }}
            errors={editErrors}
            onDataChange={handleDataChange}
            viewOnly={!isEditing}
            isEditing={isEditing}
            onEdit={handleEdit}
            onSave={() => handleSaveEdit()}
            onCancel={handleCancelEdit}
            emailValidation={isEditing ? {
              emailChecking,
              emailAvailable,
              emailMessage
            } : undefined}
          />
        </div>

        {/* Save Confirmation Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Changes</DialogTitle>
              <DialogDescription>
                Are you sure you want to save the changes to this firm? This will update the firm information permanently.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={cancelSave}>
                Cancel
              </Button>
              <Button onClick={confirmSave} className="bg-green-600 hover:bg-green-700">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DeleteModal
          open={deleteFirmModal}
          onClose={deleteFirmModalClose}
          onConfirm={deleteItem}
          title="Delete Firm"
          description="Are you sure you want to delete this firm? This action cannot be undone."
        />
      </div>
    </div>
  )
} 